import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PlatformLayout from './components/PlatformLayout';
import AnimalListPage from './pages/AnimalListPage';
import AdoptionCheckoutPage from './pages/AdoptionCheckoutPage';
import DonationPortalPage from './pages/DonationPortalPage';
import AdopterPortalPage from './pages/AdopterPortalPage';
import GamifiedMatcherPage from './pages/GamifiedMatcherPage';
import AnimalProfilePage from './pages/AnimalProfilePage';
import { LoginPage, RegisterPage, ProfilePage } from './pages/AuthPages';
import { useAuth, ErrorBoundary, NotFoundPage, ResetPasswordPage } from '@rehome/shared';

/**
 * Redirect to /login if not authenticated, or if user is not a platform user.
 *
 * A megszakított úticélt `?next=` paraméterben visszük magunkkal, és a
 * bejelentkezés után pontosan oda tesszük vissza a felhasználót. Enélkül
 * minden login fal a /animals listára dobta ki az embert, aki így elvesztette
 * azt, amit épp csinálni akart (tipikusan egy konkrét állat örökbefogadási
 * igényét egy Smart Match találat után).
 */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();
  const loginUrl = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
  if (loading) return null;
  if (!user) return <Navigate to={loginUrl} replace />;
  // Shelter users cannot access platform protected routes
  if (userProfile && userProfile.user_type !== 'platform') return <Navigate to={loginUrl} replace />;
  return <>{children}</>;
};

/**
 * ReHome Platform App (app.rehome.hu)
 *
 * Adopter-facing: browse/search animals, adoption checkout, donations,
 * post-adoption tracking, gamified matcher with optional Gemini garnish.
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route element={<PlatformLayout />}>
        <Route path="/" element={<AnimalListPage />} />
        <Route path="/animals" element={<AnimalListPage />} />
        <Route path="/animals/:animalId" element={<AnimalProfilePage />} />
        <Route path="/adoption/:animalId" element={<RequireAuth><AdoptionCheckoutPage /></RequireAuth>} />
        <Route path="/donate" element={<DonationPortalPage />} />
        <Route path="/my-animals" element={<RequireAuth><AdopterPortalPage /></RequireAuth>} />
        <Route path="/match" element={<GamifiedMatcherPage />} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
        {/* NYILVÁNOS: a jelszó-visszaállító linkről érkezők épp azért
            jönnek, mert nem tudnak belépni. */}
        <Route path="/reset-password" element={<ResetPasswordPage appName="ReHome" />} />
        <Route path="*" element={<NotFoundPage message="A keresett oldal nem található a platformon." />} />
      </Route>
    </Routes>
    </ErrorBoundary>
  );
};

export default App;
